import { useState } from 'react';
import type { PlaybookVersion } from '@reelmachine/core';
import { useIsAdmin, usePlaybook, useRollback } from '../../api/learning';
import { formatWhen } from '../../lib/format';
import { useToast } from '../../ui/Toast';
import { QueryState } from './QueryState';

const ORIGIN_LABEL: Record<PlaybookVersion['origin'], string> = {
  seed: 'Seed',
  direct: 'Applied directly',
  experiment: 'Experiment',
  decision: 'Decision',
  rollback: 'Rollback',
  manual: 'Manual',
};

function ActiveVersion({ v }: { v: PlaybookVersion }) {
  const addenda = Object.entries(v.params.addenda).filter(([, text]) => text);
  return (
    <section className="card" aria-label="Active playbook">
      <div className="row between">
        <h3>Version {v.version}</h3>
        <div className="row" style={{ gap: 6 }}>
          <span className="chip">{ORIGIN_LABEL[v.origin]}</span>
          {v.watch && (
            <span className="chip warn" title={`Watched until ${v.watch.untilReels} reels`}>
              Watched
            </span>
          )}
        </div>
      </div>
      <p>{v.note}</p>
      <span className="label">Rules</span>
      {v.params.rules.length === 0 ? (
        <p className="muted small">No rules.</p>
      ) : (
        <ul className="plain-list rules">
          {v.params.rules.map((r) => (
            <li key={r.id}>
              <div className="row between nowrap">
                <code>{r.id}</code>
                <span className={`chip ${r.status === 'active' ? 'ok' : 'rec'}`}>
                  {r.status === 'active' ? 'Active' : 'Testing'}
                </span>
              </div>
              <p>{r.text}</p>
              {r.evidence && <p className="small muted">{r.evidence}</p>}
            </li>
          ))}
        </ul>
      )}
      {addenda.length > 0 && <span className="label">Guidance per step</span>}
      {addenda.map(([step, text]) => (
        <details key={step}>
          <summary className="field-l">{step}</summary>
          <p className="small" style={{ whiteSpace: 'pre-wrap' }}>
            {text}
          </p>
        </details>
      ))}
    </section>
  );
}

function RollbackForm({ v, onDone }: { v: PlaybookVersion; onDone: () => void }) {
  const toast = useToast();
  const rollback = useRollback();
  const [note, setNote] = useState('');
  return (
    <form
      className="confirm"
      aria-label={`Roll back to version ${v.version}`}
      onSubmit={(e) => {
        e.preventDefault();
        rollback.mutate(
          { toVersion: v.version, note: note.trim(), scope: v.scope },
          {
            onSuccess: (res) => {
              toast(`Rolled back. Version ${res.version.version} is active.`);
              onDone();
            },
            onError: (err) => toast(err.message),
          },
        );
      }}
    >
      <p>The params of version {v.version} become a new active version. Reels already made keep theirs.</p>
      <div className="field">
        <label className="field-l" htmlFor={`rb-${v.version}`}>
          Why roll back?
        </label>
        <input
          id={`rb-${v.version}`}
          type="text"
          required
          maxLength={1000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div className="row nowrap">
        <button type="button" className="btn sec" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="btn" disabled={rollback.isPending || !note.trim()}>
          Confirm rollback
        </button>
      </div>
    </form>
  );
}

function HistoryRow({ v, admin }: { v: PlaybookVersion; admin: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <div className="row between nowrap">
        <strong>v{v.version}</strong>
        <div className="row" style={{ gap: 6 }}>
          <span className="chip">{ORIGIN_LABEL[v.origin]}</span>
          <span className={`chip ${v.status === 'active' ? 'ok' : ''}`}>
            {v.status[0]!.toUpperCase() + v.status.slice(1)}
          </span>
        </div>
      </div>
      <p className="small">{v.note}</p>
      <p className="nums">
        {v.createdBy.name}, {formatWhen(v.createdAt)}
      </p>
      {admin && v.status !== 'active' && v.status !== 'candidate' && !open && (
        <button type="button" className="iconbtn" onClick={() => setOpen(true)}>
          Roll back to this version
        </button>
      )}
      {open && <RollbackForm v={v} onDone={() => setOpen(false)} />}
    </li>
  );
}

export function PlaybookTab() {
  const q = usePlaybook();
  const admin = useIsAdmin();
  return (
    <QueryState query={q}>
      {(versions) => {
        const active = versions.find((v) => v.status === 'active');
        return (
          <>
            {active ? <ActiveVersion v={active} /> : <p className="muted">No active playbook.</p>}
            <section aria-label="Version history" className="field">
              <span className="label">History</span>
              <ol className="plain-list history">
                {versions.map((v) => (
                  <HistoryRow key={v.version} v={v} admin={admin} />
                ))}
              </ol>
            </section>
          </>
        );
      }}
    </QueryState>
  );
}
